// @/components/reusable/skeleton.jsx
// V2 Skeleton Components for Loading States

import React from "react";
import clsx from "clsx";

// ============================================================================
// BASE SKELETON
// ============================================================================

/**
 * Skeleton - Base skeleton element with pulse animation
 *
 * @param {string} className - Additional classes
 * @param {boolean} rounded - Apply rounded corners (default: true)
 */
const Skeleton = ({ className, rounded = true }) => {
  return (
    <div
      className={clsx(
        "animate-pulse bg-zinc-200/75",
        rounded && "rounded-sm",
        className
      )}
    />
  );
};

// ============================================================================
// SKELETON TEXT
// ============================================================================

/**
 * SkeletonText - Text placeholder skeleton
 *
 * @param {string} width - Width class (e.g., "w-32", "w-full")
 * @param {string} size - Text size: "sm" | "md" | "lg" | "xl" (default: "md")
 */
const SkeletonText = ({ width = "w-32", size = "md", className }) => {
  const heights = {
    sm: "h-3",
    md: "h-4",
    lg: "h-5",
    xl: "h-6",
  };

  return (
    <Skeleton className={clsx(heights[size], width, className)} />
  );
};

// ============================================================================
// SKELETON CIRCLE
// ============================================================================

/**
 * SkeletonCircle - Circular skeleton for avatars/icons
 *
 * @param {string} size - Size class (e.g., "size-8", "size-12")
 */
const SkeletonCircle = ({ size = "size-8", className }) => {
  return (
    <Skeleton className={clsx(size, "rounded-full", className)} rounded={false} />
  );
};

// ============================================================================
// SKELETON CARD (YEAR CARD STYLE)
// ============================================================================

/**
 * SkeletonCard - Card skeleton matching YearCard dimensions
 * Mimics the structure of a YearCard with header, content area, and emissions display
 * Pass flex-1 via className to fill available viewport height
 */
const SkeletonCard = ({ className }) => {
  return (
    <div
      className={clsx(
        // Match YearCard styling exactly
        "min-h-[448px] p-8 sm:p-10 md:p-12",
        "relative overflow-hidden flex flex-col",
        "bg-white ring-1 ring-inset ring-zinc-200/75 shadow-xs rounded-2xl",
        className
      )}
    >
      {/* Header - matches YearCardHeader layout */}
      <div className="flex items-start justify-between">
        {/* Year title skeleton */}
        <div className="flex items-center gap-3">
          <Skeleton className="h-8 w-24 sm:h-9 sm:w-28" />
          <Skeleton className="h-5 w-20" />
        </div>
        {/* Action buttons skeleton */}
        <div className="flex items-center gap-2">
          <SkeletonCircle size="size-8" />
          <SkeletonCircle size="size-8" />
        </div>
      </div>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Content - matches YearCardContent layout */}
      <div className="space-y-6">
        {/* Total emissions */}
        <div className="space-y-2">
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-12 w-48 sm:h-14 sm:w-56" />
        </div>

        {/* Scope breakdown bars */}
        <div className="flex gap-1.5 h-3">
          <Skeleton className="flex-3 rounded-full" rounded={false} />
          <Skeleton className="flex-2 rounded-full" rounded={false} />
          <Skeleton className="flex-5 rounded-full" rounded={false} />
        </div>

        {/* Scope labels */}
        <div className="flex justify-between">
          <div className="space-y-1">
            <Skeleton className="h-3 w-14" />
            <Skeleton className="h-4 w-20" />
          </div>
          <div className="space-y-1">
            <Skeleton className="h-3 w-14" />
            <Skeleton className="h-4 w-20" />
          </div>
          <div className="space-y-1">
            <Skeleton className="h-3 w-14" />
            <Skeleton className="h-4 w-20" />
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// EXPORTS
// ============================================================================

export { Skeleton, SkeletonText, SkeletonCircle, SkeletonCard };
export default Skeleton;
